import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/*
 * TimezoneIndicator — DESIGN_SYSTEM.md §2.4
 *
 * Pill showing the organization's timezone:
 *   • status dot (status-available)
 *   • globe icon
 *   • "City · GMT±N" caption (tabular numerics)
 *
 * Hidden below `lg` by default to keep mobile TopBar compact.
 */

interface TimezoneIndicatorProps {
  timezone?: string | null;
  className?: string;
}

function getOffset(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

export function TimezoneIndicator({ timezone, className }: TimezoneIndicatorProps) {
  const { t } = useTranslation('registration');
  if (!timezone) return null;

  const fallbackCity = timezone.split('/').pop()?.replace(/_/g, ' ') ?? timezone;
  const translated = t(`timezones.${timezone}`, { defaultValue: fallbackCity });
  const city = translated.includes(' (') ? translated.split(' (')[0] : translated;
  const offset = getOffset(timezone);
  return (
    <div
      title={timezone}
      className={cn(
        'hidden lg:inline-flex items-center gap-2 h-8 px-3 rounded-pill bg-surface-2 text-body-xs text-soft whitespace-nowrap shrink-0',
        className,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-status-available" aria-hidden />
      <Globe className="h-3 w-3 opacity-60" aria-hidden />
      <span className="tabular-nums">
        {city}
        {offset && <> · {offset}</>}
      </span>
    </div>
  );
}
